import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { buildStompFrame, parseStompFrame } from '../utils/stomp-frame.utils';

export interface UserSocketConnectParams {
  apiUrl: string;
  authTelegramData?: string | null;
  xAccountId?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class UserSocketService {
  private static readonly _socketPath = '/api/v1/users/me/ws';
  private static readonly _userQueueDestination = '/user/queue/users/me';
  private static readonly _subscriptionId = 'users-me-subscription';
  private static readonly _requestDestination = '/app/users/me';
  private _userSocket: WebSocket | null = null;
  private _socketReconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private _socketReconnectDelayMs: number = 5000;
  private _lastConnectParams: UserSocketConnectParams | null = null;
  private _allowReconnect: boolean = false;
  private _stompConnected: boolean = false;

  private readonly _messageSubject = new Subject<string>();
  private readonly _errorSubject = new Subject<Event | CloseEvent>();

  readonly message$ = this._messageSubject.asObservable();
  readonly error$ = this._errorSubject.asObservable();

  connect(params: UserSocketConnectParams): void {
    this.disconnect();
    this._lastConnectParams = params;
    this._allowReconnect = true;
    this._openSocket(params);
  }

  disconnect(): void {
    this._allowReconnect = false;
    this._clearReconnectTimer();
    this._closeSocket();
  }

  private _openSocket(params: UserSocketConnectParams): void {
    const socketUrl = this._buildSocketUrl(params);
    if (!socketUrl) {
      return;
    }

    this._clearReconnectTimer();
    this._closeSocket();

    try {
      this._userSocket = new WebSocket(socketUrl);
    } catch {
      this._scheduleReconnect();
      return;
    }

    this._userSocket.onopen = (): void => {
      this._stompConnected = false;
      this._sendConnectFrame(params);
    };

    this._userSocket.onmessage = (event: MessageEvent<string>): void => {
      if (event.data) {
        this._handleSocketData(event.data, params);
      }
    };

    this._userSocket.onerror = (event: Event): void => {
      this._errorSubject.next(event);
      this._closeSocket();
      this._scheduleReconnect();
    };

    this._userSocket.onclose = (event: CloseEvent): void => {
      this._errorSubject.next(event);
      this._closeSocket();
      this._scheduleReconnect();
    };
  }

  private _scheduleReconnect(): void {
    if (!this._allowReconnect || this._socketReconnectTimer || !this._lastConnectParams) {
      return;
    }

    this._clearReconnectTimer();
    this._socketReconnectTimer = setTimeout((): void => {
      this._socketReconnectTimer = null;
      if (this._allowReconnect && this._lastConnectParams) {
        this._openSocket(this._lastConnectParams);
      }
    }, this._socketReconnectDelayMs);
  }

  private _clearReconnectTimer(): void {
    if (this._socketReconnectTimer) {
      clearTimeout(this._socketReconnectTimer);
      this._socketReconnectTimer = null;
    }
  }

  private _closeSocket(): void {
    if (this._userSocket) {
      this._userSocket.onopen = null;
      this._userSocket.onmessage = null;
      this._userSocket.onerror = null;
      this._userSocket.onclose = null;
      this._userSocket.close();
      this._userSocket = null;
      this._stompConnected = false;
    }
  }

  private _buildSocketUrl(params: UserSocketConnectParams): string | null {
    const wsBaseUrl = params.apiUrl.replace(/^http/i, 'ws');
    const socketUrl = new URL(UserSocketService._socketPath, wsBaseUrl);

    return socketUrl.toString();
  }

  private _handleSocketData(rawData: string, params: UserSocketConnectParams): void {
    const frames = rawData
      .split('\u0000')
      .map((frame: string) => frame.trim())
      .filter(Boolean);

    for (const frame of frames) {
      const { command, body } = parseStompFrame(frame);

      if (command === 'CONNECTED') {
        this._stompConnected = true;
        this._sendSubscribeFrame(params);
        this._sendRequestFrame();
        continue;
      }

      if (command === 'MESSAGE') {
        this._messageSubject.next(body);
        continue;
      }

      if (command === 'ERROR') {
        this._errorSubject.next(new Event('error'));
      }
    }
  }

  private _sendConnectFrame(params: UserSocketConnectParams): void {
    const headers: Record<string, string> = {
      'accept-version': '1.2',
      'heart-beat': '10000,10000',
      ...this._buildAuthHeaders(params)
    };

    this._sendStompFrame('CONNECT', headers);
  }

  private _sendSubscribeFrame(params: UserSocketConnectParams): void {
    if (!this._stompConnected) {
      return;
    }

    const headers: Record<string, string> = {
      id: UserSocketService._subscriptionId,
      destination: UserSocketService._userQueueDestination,
      ack: 'auto'
    };

    this._sendStompFrame('SUBSCRIBE', headers);
  }

  private _sendRequestFrame(): void {
    if (!this._stompConnected) {
      return;
    }

    this._sendStompFrame('SEND', {
      destination: UserSocketService._requestDestination
    });
  }

  private _buildAuthHeaders(params: UserSocketConnectParams): Record<string, string> {
    const headers: Record<string, string> = {};

    if (params.authTelegramData) {
      headers['X-Telegram-Init-Data'] = params.authTelegramData;
      // headers['x-auth-session'] = 'aaf4b22f-a813-4961-99a6-a507141a0cf6';
    }

    if (params.xAccountId) {
      headers['X-Account-Id'] = params.xAccountId;
      // headers['X-account-Id'] ='ACCOUNT-31d4c1fe-f2b4-4634-872f-5be141ff247c';
    }

    return headers;
  }

  private _sendStompFrame(command: string, headers: Record<string, string>, body: string = ''): void {
    if (!this._userSocket || this._userSocket.readyState !== WebSocket.OPEN) {
      return;
    }

    const frame = buildStompFrame(command, headers, body);
    this._userSocket.send(frame);
  }
}
